import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminTestEditor = ({ lectureId, lectureTitle, onSave, onCancel }) => {
    const [testData, setTestData] = useState(null);
    const [initialQuestionIds, setInitialQuestionIds] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTest = async () => {
            const token = localStorage.getItem('app_token');
            try {
                const response = await axios.get(`/api/admin/lectures/${lectureId}/test`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setTestData(response.data);
                if (response.data && response.data.questions) {
                    setInitialQuestionIds(new Set(response.data.questions.map(q => q.id)));
                }
            } catch (error) {
                console.error("Failed to fetch test data", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchTest();
    }, [lectureId]);

    const handleTestDetailChange = (e) => {
        const { name, value } = e.target;
        setTestData(prev => ({ ...prev, [name]: value }));
    };

    const handleQuestionChange = (questionId, field, value) => {
        setTestData(prev => ({
            ...prev,
            questions: prev.questions.map(q => {
                if (q.id !== questionId) return q;

                if (field === 'question_type') {
                    let newOptions = [];
                    if (value === 'true-false') {
                        newOptions = [
                            { id: `new-opt-${Date.now()}-1`, option_text: 'True', is_correct: false },
                            { id: `new-opt-${Date.now()}-2`, option_text: 'False', is_correct: false }
                        ];
                    } else if (value === 'fill-in-blank') {
                        newOptions = [{ id: `new-opt-${Date.now()}`, option_text: '', is_correct: true }];
                    }
                    return { ...q, question_type: value, options: newOptions };
                }
                return { ...q, [field]: value };
            })
        }));
    };

    const handleOptionChange = (questionId, optionId, value) => {
        setTestData(prev => ({
            ...prev,
            questions: prev.questions.map(q =>
                q.id === questionId ? {
                    ...q,
                    options: q.options.map(opt =>
                        opt.id === optionId ? { ...opt, option_text: value } : opt
                    )
                } : q
            )
        }));
    };

    const handleCorrectToggle = (questionId, optionId, questionType) => {
        setTestData(prev => ({
            ...prev,
            questions: prev.questions.map(q => {
                if (q.id !== questionId) return q;

                let newOptions = [];
                if (questionType === 'mcq-single' || questionType === 'true-false') {
                    newOptions = q.options.map(opt => ({ ...opt, is_correct: opt.id === optionId }));
                } else { // mcq-multiple
                    newOptions = q.options.map(opt =>
                        opt.id === optionId ? { ...opt, is_correct: !opt.is_correct } : opt
                    );
                }
                return { ...q, options: newOptions };
            })
        }));
    };

    const handleAddQuestion = () => {
        const newQuestion = {
            id: `new-${Date.now()}`,
            question_text: '',
            question_type: 'mcq-single',
            points: 5, // Default points for a new question
            question_order: testData.questions.length,
            options: []
        };
        setTestData(prev => ({ ...prev, questions: [...prev.questions, newQuestion] }));
    };

    const handleAddOption = (questionId) => {
        const newOption = { id: `new-opt-${Date.now()}`, option_text: 'New Option', is_correct: false };
        setTestData(prev => ({
            ...prev,
            questions: prev.questions.map(q =>
                q.id === questionId ? { ...q, options: [...(q.options || []), newOption] } : q
            )
        }));
    };

    const handleDeleteQuestion = (questionId) => {
        if (!window.confirm("Are you sure you want to delete this question?")) return;
        setTestData(prev => ({
            ...prev,
            questions: prev.questions.filter(q => q.id !== questionId)
        }));
    };

    const handleDeleteOption = (questionId, optionId) => {
        setTestData(prev => ({
            ...prev,
            questions: prev.questions.map(q =>
                q.id === questionId ? {
                    ...q,
                    options: q.options.filter(opt => opt.id !== optionId)
                } : q
            )
        }));
    };

    const handleSaveTest = async () => {
        const token = localStorage.getItem('app_token');
        const headers = { Authorization: `Bearer ${token}` };

        try {
            setIsLoading(true);
            await axios.put(`/api/admin/tests/${testData.id}`, {
                title: testData.title,
                passing_score: testData.passing_score
            }, { headers });

            const currentQuestionIds = new Set(testData.questions.map(q => q.id));
            const deletedQuestionIds = [...initialQuestionIds].filter(id => !currentQuestionIds.has(id));

            for (const questionId of deletedQuestionIds) {
                await axios.delete(`/api/admin/questions/${questionId}`, { headers });
            }

            for (const question of testData.questions) {
                let currentQuestionId = question.id;
                let questionPayload = {
                    question_text: question.question_text,
                    question_type: question.question_type,
                    points: question.points, // Include points in the payload
                    question_order: question.question_order
                };

                if (String(currentQuestionId).startsWith('new-')) {
                    const response = await axios.post(`/api/admin/tests/${testData.id}/questions`, questionPayload, { headers });
                    currentQuestionId = response.data.id;
                } else {
                    await axios.put(`/api/admin/questions/${currentQuestionId}`, questionPayload, { headers });
                }

                if (question.options?.length > 0) {
                    await axios.post(`/api/admin/questions/${currentQuestionId}/options`, {
                        options: question.options.map(({ option_text, is_correct }) => ({ option_text, is_correct }))
                    }, { headers });
                }
            }

            alert('Test saved successfully!');
            onSave();

        } catch (error) {
            console.error("Error saving test", error);
            alert("Failed to save the test. Check the console for details.");
        } finally {
            setIsLoading(false);
        }
    };

    const renderOptionEditor = (question) => {
        switch (question.question_type) {
            case 'mcq-single':
            case 'mcq-multiple':
                return (
                    <div className="question-options-list">
                        <label>Options</label>
                        {question.options && question.options.map(opt => (
                            <div key={opt.id} className="option-item">
                                <input type="text" value={opt.option_text} onChange={(e) => handleOptionChange(question.id, opt.id, e.target.value)} />
                                <div className="option-correct-toggle" onClick={() => handleCorrectToggle(question.id, opt.id, question.question_type)}>
                                    <span className="material-icons">
                                        {opt.is_correct ? (question.question_type === 'mcq-multiple' ? 'check_box' : 'radio_button_checked') : (question.question_type === 'mcq-multiple' ? 'check_box_outline_blank' : 'radio_button_unchecked')}
                                    </span>
                                    <span>Correct</span>
                                </div>
                                <button onClick={() => handleDeleteOption(question.id, opt.id)} className="action-btn delete"><span className="material-icons">remove_circle_outline</span></button>
                            </div>
                        ))}
                        <button onClick={() => handleAddOption(question.id)} className="btn-secondary" style={{ alignSelf: 'flex-start' }}>Add Option</button>
                    </div>
                );

            case 'true-false':
                return (
                    <div className="question-options-list">
                        <label>Options</label>
                        {question.options && question.options.map(opt => (
                            <div key={opt.id} className="option-item">
                                <input type="text" value={opt.option_text} readOnly disabled />
                                <div className="option-correct-toggle" onClick={() => handleCorrectToggle(question.id, opt.id, question.question_type)}>
                                    <span className="material-icons">
                                        {opt.is_correct ? 'radio_button_checked' : 'radio_button_unchecked'}
                                    </span>
                                    <span>Correct</span>
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'fill-in-blank':
                return (
                     <div className="question-options-list">
                        <label>Correct Answer</label>
                        <p style={{ fontSize: '0.8rem', margin: '-0.5rem 0 0.5rem', color: '#64748b' }}>You can add multiple correct answers by separating them with a pipe character (|). e.g., "answer1|answer2"</p>
                        <input
                            type="text"
                            className="admin-form-input"
                            value={question.options?.[0]?.option_text || ''}
                            onChange={(e) => handleOptionChange(question.id, question.options?.[0]?.id, e.target.value)}
                            placeholder="Enter the correct answer(s) here"
                        />
                    </div>
                );

            default:
                return null;
        }
    };
    
    if (isLoading) return <p>Loading test editor...</p>;
    if (!testData) return <p>Could not load the test for this lecture. Please ensure it was saved correctly.</p>;

    return (
        <div className="admin-modal-overlay nested-modal-overlay">
            <div className="admin-modal-content">
                <h2>Test Editor: {lectureTitle}</h2>
                <div className="test-editor-container">
                    <div className="test-details-form">
                        <div className="form-group" style={{ flexGrow: 1 }}>
                            <label>Test Title</label>
                            <input type="text" name="title" value={testData.title} onChange={handleTestDetailChange} />
                        </div>
                        <div className="form-group">
                            <label>Passing Score (%)</label>
                            <input type="number" name="passing_score" value={testData.passing_score} onChange={handleTestDetailChange} min="0" max="100" />
                        </div>
                    </div>
                    
                    {testData.questions.map((q, index) => (
                        <div key={q.id} className="question-card">
                            <div className="question-header">
                                <h5>Question {index + 1}</h5>
                                <div className="form-group">
                                    <label>Points</label>
                                    <input 
                                        type="number" 
                                        value={q.points || 5} 
                                        onChange={(e) => handleQuestionChange(q.id, 'points', parseInt(e.target.value, 10))}
                                        style={{width: '80px'}}
                                    />
                                </div>
                                <button onClick={() => handleDeleteQuestion(q.id)} className="action-btn delete" title="Delete Question"><span className="material-icons">delete</span></button>
                            </div>
                            <div className="form-group">
                                <label>Question Text</label>
                                <textarea className="admin-textarea" rows="3" value={q.question_text} onChange={(e) => handleQuestionChange(q.id, 'question_text', e.target.value)} />
                            </div>
                             <div className="form-group">
                                <label>Question Type</label>
                                <select value={q.question_type} onChange={(e) => handleQuestionChange(q.id, 'question_type', e.target.value)}>
                                    <option value="mcq-single">Multiple Choice (Single Answer)</option>
                                    <option value="mcq-multiple">Multiple Choice (Multiple Answers)</option>
                                    <option value="true-false">True/False</option>
                                    <option value="fill-in-blank">Fill in the Blank</option>
                                </select>
                            </div>
                            {renderOptionEditor(q)}
                        </div>
                    ))}
                    <button onClick={handleAddQuestion} className="btn-primary" style={{marginTop: '1rem'}}>Add New Question</button>
                </div>
                <div className="form-actions">
                    <button onClick={onCancel} className="btn-secondary" disabled={isLoading}>Cancel</button>
                    <button onClick={handleSaveTest} className="btn-primary" disabled={isLoading}>
                        {isLoading ? 'Saving...' : 'Save Entire Test'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminTestEditor;
